/**
 * accounting.js — the double-entry engine.
 *
 * WHAT CHANGED, AND WHY IT HAD TO
 *
 * This application derived its statements from documents. A sales invoice was
 * a record of a sale; the profit and loss added the invoices up. That produces
 * correct figures for the things it tracks and it cannot produce a Trial
 * Balance, because there is nothing to balance — there was one posting per
 * invoice, not two, and a balance sheet had to be constructed with owner's
 * equity as the plug.
 *
 * This module is the replacement. EVERY financial event in the application now
 * produces a VOUCHER, and every voucher produces two or more ACCOUNTING
 * ENTRIES whose debits equal their credits. The ledgers, the trial balance, the
 * profit and loss, the balance sheet and every outstanding report are then
 * DERIVED from those entries and from nothing else. A figure that cannot be
 * traced to an entry is a figure this application does not print.
 *
 * THE ONE RULE
 * Total debit equals total credit. Enforced here, enforced again in the
 * repository's transaction, and enforced a third time in `firestore.rules` —
 * three times because a hole in the books is inherited by every report
 * downstream and there is no cheap moment to catch it later.
 *
 * WHAT A LINE CAN NAME
 * Exactly one thing: a LEDGER. There is no "target" discriminator, no account /
 * party / category split, and that is the single largest simplification here.
 * A customer is a ledger under Sundry Debtors. The till is a ledger under
 * Cash-in-Hand. Electricity is a ledger under Indirect Expenses. One kind of
 * thing means one kind of posting, one kind of balance, and a trial balance
 * that can list everything without asking what sort of row it is looking at.
 *
 * Everything here is PURE. Money is integer paise. The writes live in
 * features/accounting/posting-repository.js.
 */

import { round } from './money';
import { formatMoney } from './format';
import { financialYear } from './fiscal';
import { GROUP_FLAGS, GROUP_IDS, NATURES } from './ledger-groups';
import { SIDES, signedAmount } from './ledgers';

export { SIDES };

/* ------------------------------------------------------------------ *
 * Voucher types
 *
 * The names are the standard accounting ones, deliberately. The specification
 * is explicit that Contra must be called Contra and not "Bank/Cash Transfer",
 * and it is right: an accountant looking for the transfer register looks for
 * Contra, and a shopkeeper who learns the word once has learned the word that
 * every other book they will ever see uses.
 * ------------------------------------------------------------------ */

export const VOUCHER_TYPES = {
  JOURNAL: 'journal',
  PAYMENT: 'payment',
  RECEIPT: 'receipt',
  CONTRA: 'contra',
  SALES: 'sales',
  PURCHASE: 'purchase',
  CREDIT_NOTE: 'credit_note',
  DEBIT_NOTE: 'debit_note',
  EXPENSE: 'expense',
  INCOME: 'income',
  OPENING: 'opening',
  ADJUSTMENT: 'adjustment',
  REVERSAL: 'reversal',
  STOCK_JOURNAL: 'stock_journal',
};

/**
 * `entryMode` is what the voucher screen looks like, and it is the field that
 * keeps the editor to three shapes instead of fourteen:
 *
 *   DOUBLE   a free Dr/Cr grid. Journal, adjustment, opening, reversal.
 *   SINGLE   one side is fixed by the voucher type — a Payment always credits
 *            cash or bank and debits everything else — so the grid asks for the
 *            "particulars" rows and one "paid from" ledger. This is Tally's
 *            single-entry mode and it is what makes a payment three keystrokes
 *            instead of six.
 *   SOURCED  the voucher is the accounting shadow of a billing document. It is
 *            not entered here; editing it opens the invoice.
 */
export const ENTRY_MODES = {
  DOUBLE: 'double',
  SINGLE: 'single',
  SOURCED: 'sourced',
};

export const VOUCHER_CONFIG = {
  [VOUCHER_TYPES.JOURNAL]: {
    type: VOUCHER_TYPES.JOURNAL,
    label: 'Journal',
    plural: 'Journal',
    route: 'journal',
    prefix: 'JV',
    entryMode: ENTRY_MODES.DOUBLE,
    /** Adjustments, accruals, depreciation, rectification, reclassification. */
    description:
      'The entry no document describes — depreciation, an accrual, a provision, a correction, a reclassification.',
    /**
     * A JOURNAL MAY NOT TOUCH CASH OR BANK.
     *
     * This used to be advisory — `warnOnCash: true`, a hint the user could
     * post straight past. It is now refused, in the grid and at validation.
     *
     * The reason is that the cash book and the bank book are not reports built
     * by filtering the journal; they are what the Payment, Receipt and Contra
     * registers mean. A journal entry that credits Bank moves the balance
     * without ever appearing in the register the balance is reconciled from,
     * so the statement and the books disagree and nothing in the software says
     * why. Every legitimate case has its own voucher type — money out is a
     * Payment, money in a Receipt, money between our own accounts a Contra —
     * so refusing this takes nothing away.
     */
    forbidFlag: GROUP_FLAGS.CASH,
    forbidMessage:
      'A Journal cannot move cash or bank. Money out is a Payment, money in is a Receipt, and money between your own accounts is a Contra.',
    /* The two cards this type is entered in. A Journal has no fixed side, so
       the cards are simply the two sides — which is how a journal is written
       and read: the debits, then the accounts credited against them. */
    debitLabel: 'Debit',
    debitHint: 'The accounts debited.',
    creditLabel: 'Credit',
    creditHint: 'The accounts credited against them.',
    tone: 'neutral',
    counterType: 'acc_journal',
  },

  [VOUCHER_TYPES.PAYMENT]: {
    type: VOUCHER_TYPES.PAYMENT,
    label: 'Payment',
    plural: 'Payments',
    route: 'payment',
    prefix: 'PAY',
    entryMode: ENTRY_MODES.SINGLE,
    description: 'Money going out — to a supplier, for an expense, as an advance.',
    /** The fixed side: money leaves cash or bank, so that ledger is CREDITED. */
    fixedSide: SIDES.CREDIT,
    fixedLedgerFlag: GROUP_FLAGS.CASH,
    fixedLabel: 'Paid from',
    fixedHint: 'The cash or bank account the money left. Add a row for each, if it came out of more than one.',
    particularsLabel: 'Paid to / for',
    particularsHint: 'What the money was for, or who it was paid to.',
    /**
     * ONE SIDE OF A PAYMENT IS ALWAYS CASH OR BANK. Money left the business;
     * it left from somewhere. Enforced rather than assumed, because the fixed
     * ledger is only demanded when the grid does not already balance — which
     * let a user write both sides by hand, balance them between two supplier
     * ledgers, and post a "payment" in which no money moved at all.
     */
    requiresCashSide: true,
    cashSideMessage:
      'A Payment must say where the money went out of. Choose a cash or bank account as the paid-from side.',
    /**
     * WHAT MAY BE PICKED ON EACH SIDE. See `ledgerAllowedOnSide` below for the
     * shape and for why these shape the picker without being enforced.
     *
     * Debit — what the money was FOR: a supplier, an expense, an asset bought,
     * a loan repaid, drawings. Never income: money going out does not debit an
     * income account. Reducing income already booked is a credit note, or a
     * Journal if there is no document.
     *
     * Credit — the PAID-FROM side, and left wide open.
     *
     * Cash and bank belong here and nowhere else on this voucher. The Paid
     * from field is the shortcut for the usual case of one source; a payment
     * settled ₹3,000 in cash and ₹2,000 by cheque has TWO, and the second one
     * has to be a row. Forbidding cash here — as this rule briefly did —
     * made the split payment unenterable, which is not a rule, it is a bug.
     *
     * A discount received or a rounding lands here too. Broad on purpose: the
     * entries that belong on the credit side of a payment are too varied to
     * enumerate, and guessing wrong blocks them.
     *
     * Cash stays off the DEBIT side. Money arriving in a cash account is a
     * Receipt, and money moving from one of your accounts to another is a
     * Contra — neither is a Payment, and both have a register of their own.
     */
    sideRules: {
      [SIDES.DEBIT]: { forbidFlags: [GROUP_FLAGS.CASH], forbidNatures: [NATURES.INCOME] },
      [SIDES.CREDIT]: {},
    },
    tone: 'danger',
    counterType: 'acc_payment',
  },

  [VOUCHER_TYPES.RECEIPT]: {
    type: VOUCHER_TYPES.RECEIPT,
    label: 'Receipt',
    plural: 'Receipts',
    route: 'receipt',
    prefix: 'REC',
    entryMode: ENTRY_MODES.SINGLE,
    description: 'Money coming in — from a customer, as income, as an advance.',
    /** Money arrives in cash or bank, so that ledger is DEBITED. */
    fixedSide: SIDES.DEBIT,
    fixedLedgerFlag: GROUP_FLAGS.CASH,
    fixedLabel: 'Received into',
    fixedHint: 'The cash or bank account the money arrived in. Add a row for each, if it went into more than one.',
    particularsLabel: 'Received from / for',
    particularsHint: 'Who the money came from, or what it was earned as.',
    /** The mirror of the Payment rule: money arrived, so it arrived somewhere. */
    requiresCashSide: true,
    cashSideMessage:
      'A Receipt must say where the money came into. Choose a cash or bank account as the received-into side.',
    /**
     * The mirror of the Payment rules.
     *
     * Credit — where the money came FROM: a customer, income earned, a loan
     * taken, capital introduced, an asset sold. Never an expense: money coming
     * in does not credit an expense account. Recovering a cost already booked
     * is a debit note, or a Journal. Never cash: money arriving in one of your
     * accounts and leaving another is a Contra.
     *
     * Debit — the RECEIVED-INTO side, open for the same reason the Payment's
     * credit side is: ₹5,000 taken as ₹3,000 cash and ₹2,000 into the bank is
     * one receipt with two destinations, and the Received into field holds
     * only one. A discount allowed or a bank charge deducted lands here too.
     */
    sideRules: {
      [SIDES.DEBIT]: {},
      [SIDES.CREDIT]: { forbidFlags: [GROUP_FLAGS.CASH], forbidNatures: [NATURES.EXPENSE] },
    },
    tone: 'success',
    counterType: 'acc_receipt',
  },

  [VOUCHER_TYPES.CONTRA]: {
    type: VOUCHER_TYPES.CONTRA,
    label: 'Contra',
    plural: 'Contra',
    route: 'contra',
    prefix: 'CON',
    entryMode: ENTRY_MODES.DOUBLE,
    description: 'Money moving between your own accounts — cash to bank, bank to bank.',
    /**
     * The one type with a restriction worth enforcing rather than warning
     * about. A contra that touches a supplier is not a contra, it is a payment,
     * and letting it through means the transfer register no longer answers the
     * question it exists to answer.
     */
    restrictToFlag: GROUP_FLAGS.CASH,
    restrictionMessage:
      'A Contra moves money between your own cash and bank accounts. To pay a party or a bill, use a Payment.',
    /**
     * "To" and "From", not "Debit" and "Credit".
     *
     * A contra is one movement with two ends, and the ends are what the
     * operator knows: cash went INTO the bank, out OF the till. `Cash A/c Dr.
     * / To Bank A/c` is the same fact, and the Entry card below states it that
     * way — but nobody deposits money by thinking "debit". The accounting
     * remains exactly what it was; only the heading is in the operator's
     * words.
     */
    debitLabel: 'Transferred to',
    debitHint: 'The account the money arrived in.',
    creditLabel: 'Transferred from',
    creditHint: 'The account the money left.',
    tone: 'neutral',
    counterType: 'acc_contra',
  },

  [VOUCHER_TYPES.SALES]: {
    type: VOUCHER_TYPES.SALES,
    label: 'Sales',
    plural: 'Sales',
    route: 'sales',
    prefix: 'INV',
    entryMode: ENTRY_MODES.SOURCED,
    description: 'The accounting entry behind a sales invoice.',
    sourceRoute: 'sales',
    tone: 'primary',
  },
  [VOUCHER_TYPES.PURCHASE]: {
    type: VOUCHER_TYPES.PURCHASE,
    label: 'Purchase',
    plural: 'Purchases',
    route: 'purchase',
    prefix: 'PUR',
    entryMode: ENTRY_MODES.SOURCED,
    description: 'The accounting entry behind a purchase bill.',
    sourceRoute: 'purchases',
    tone: 'primary',
  },
  [VOUCHER_TYPES.CREDIT_NOTE]: {
    type: VOUCHER_TYPES.CREDIT_NOTE,
    label: 'Credit Note',
    plural: 'Credit Notes',
    route: 'credit-note',
    prefix: 'CRN',
    entryMode: ENTRY_MODES.SOURCED,
    description: 'The accounting entry behind a credit note or sales return.',
    sourceRoute: 'sales-return',
    tone: 'warning',
  },
  [VOUCHER_TYPES.DEBIT_NOTE]: {
    type: VOUCHER_TYPES.DEBIT_NOTE,
    label: 'Debit Note',
    plural: 'Debit Notes',
    route: 'debit-note',
    prefix: 'DBN',
    entryMode: ENTRY_MODES.SOURCED,
    description: 'The accounting entry behind a debit note or purchase return.',
    sourceRoute: 'purchase-return',
    tone: 'warning',
  },

  [VOUCHER_TYPES.EXPENSE]: {
    type: VOUCHER_TYPES.EXPENSE,
    label: 'Expense',
    plural: 'Expenses',
    route: 'expense',
    prefix: 'EXP',
    entryMode: ENTRY_MODES.SINGLE,
    description: 'A cost paid or payable, without a supplier bill behind it.',
    fixedSide: SIDES.CREDIT,
    fixedLedgerFlag: GROUP_FLAGS.CASH,
    fixedLabel: 'Paid from',
    fixedHint: 'The cash or bank account the money left.',
    particularsLabel: 'Expense head',
    particularsHint: 'The cost this was.',
    /* A Payment by another name — same sides, same reasoning. */
    sideRules: {
      [SIDES.DEBIT]: { forbidFlags: [GROUP_FLAGS.CASH], forbidNatures: [NATURES.INCOME] },
      [SIDES.CREDIT]: {},
    },
    tone: 'danger',
    counterType: 'acc_expense',
  },
  [VOUCHER_TYPES.INCOME]: {
    type: VOUCHER_TYPES.INCOME,
    label: 'Income',
    plural: 'Income',
    route: 'income',
    prefix: 'INC',
    entryMode: ENTRY_MODES.SINGLE,
    description: 'Income received that is not a sale — interest, commission, rent.',
    fixedSide: SIDES.DEBIT,
    fixedLedgerFlag: GROUP_FLAGS.CASH,
    fixedLabel: 'Received into',
    fixedHint: 'The cash or bank account the money arrived in.',
    particularsLabel: 'Income head',
    particularsHint: 'What the money was earned as.',
    /* A Receipt by another name. */
    sideRules: {
      [SIDES.DEBIT]: {},
      [SIDES.CREDIT]: { forbidFlags: [GROUP_FLAGS.CASH], forbidNatures: [NATURES.EXPENSE] },
    },
    tone: 'success',
    counterType: 'acc_income',
  },

  [VOUCHER_TYPES.OPENING]: {
    type: VOUCHER_TYPES.OPENING,
    label: 'Opening Balance',
    plural: 'Opening Balances',
    route: 'opening',
    prefix: 'OB',
    entryMode: ENTRY_MODES.DOUBLE,
    description: 'The balances the books were brought onto the system with.',
    /** Opening entries are dated the first day of the year and stay there. */
    pinToYearStart: true,
    debitLabel: 'Debit',
    debitHint: 'The accounts debited.',
    creditLabel: 'Credit',
    creditHint: 'The accounts credited against them.',
    tone: 'neutral',
    counterType: 'acc_opening',
  },
  [VOUCHER_TYPES.ADJUSTMENT]: {
    type: VOUCHER_TYPES.ADJUSTMENT,
    label: 'Adjustment',
    plural: 'Adjustments',
    route: 'adjustment',
    prefix: 'ADJ',
    entryMode: ENTRY_MODES.DOUBLE,
    description: 'A correction or reclassification linked to the entry it fixes.',
    debitLabel: 'Debit',
    debitHint: 'The accounts debited.',
    creditLabel: 'Credit',
    creditHint: 'The accounts credited against them.',
    tone: 'neutral',
    counterType: 'acc_adjustment',
  },
  [VOUCHER_TYPES.REVERSAL]: {
    type: VOUCHER_TYPES.REVERSAL,
    label: 'Reversal',
    plural: 'Reversals',
    route: 'reversal',
    prefix: 'REV',
    entryMode: ENTRY_MODES.DOUBLE,
    description: 'The opposite of an entry, posted beside it so both stay visible.',
    debitLabel: 'Debit',
    debitHint: 'The accounts debited.',
    creditLabel: 'Credit',
    creditHint: 'The accounts credited against them.',
    tone: 'danger',
    counterType: 'acc_reversal',
  },
  [VOUCHER_TYPES.STOCK_JOURNAL]: {
    type: VOUCHER_TYPES.STOCK_JOURNAL,
    label: 'Stock Journal',
    plural: 'Stock Journal',
    route: 'stock-journal',
    prefix: 'STK',
    entryMode: ENTRY_MODES.SOURCED,
    description: 'The accounting entry behind a stock adjustment or a production run.',
    tone: 'neutral',
  },
};

/** The types a user may raise from the Vouchers menu, in menu order. */
export const ENTERABLE_TYPES = [
  VOUCHER_TYPES.PAYMENT,
  VOUCHER_TYPES.RECEIPT,
  VOUCHER_TYPES.CONTRA,
  VOUCHER_TYPES.JOURNAL,
  VOUCHER_TYPES.EXPENSE,
  VOUCHER_TYPES.INCOME,
  VOUCHER_TYPES.ADJUSTMENT,
  VOUCHER_TYPES.OPENING,
];

export const ALL_TYPES = Object.keys(VOUCHER_CONFIG);

export function voucherConfig(type) {
  return VOUCHER_CONFIG[type] || VOUCHER_CONFIG[VOUCHER_TYPES.JOURNAL];
}

export function tryVoucherConfig(type) {
  return VOUCHER_CONFIG[type] || null;
}

export function voucherTypeFromRoute(route) {
  const found = ALL_TYPES.find((type) => VOUCHER_CONFIG[type].route === route);
  return found || null;
}

/** True when the voucher is the shadow of a billing document. */
export const isSourced = (type) => voucherConfig(type).entryMode === ENTRY_MODES.SOURCED;

/**
 * The two cards a voucher is entered in, debits first.
 *
 * EVERY enterable type is two cards of rows now — one per side — and this is
 * the one place that decides what each is called. Payment and Receipt name
 * their sides after the money ("Paid to / for", "Paid from"); Contra names its
 * ends ("Transferred to", "Transferred from"); a Journal simply says Debit and
 * Credit, because that is what a journal is.
 *
 * WHY THERE IS NO Dr/Cr CONTROL ANY MORE. A card IS a side. The grid used to
 * carry a two-button toggle on every row so the operator could say which side
 * it was — necessary when both sides shared one table, and pure noise once
 * they do not: on a card headed "Transferred from" every row is a credit, and
 * a control that can only be set one way is a control that can only be set
 * wrongly. The side moved from a widget the user has to operate to a heading
 * they only have to read.
 *
 * DEBITS FIRST, always. It is the order a book writes an entry, and the order
 * `JournalEntryView` prints it in below the cards — two orderings of one
 * voucher on one screen would be worse than either.
 *
 * @returns {Array<{side, title, hint, group}>} empty for a SOURCED type, which
 *          is not entered here at all.
 */
export function sideCards(config) {
  if (!config || config.entryMode === ENTRY_MODES.SOURCED) return [];

  const fixed = config.fixedSide;
  const named = (side) => {
    /* A SINGLE type has a fixed side and a particulars side, each already
       named for what it holds; a DOUBLE type names both sides outright. */
    if (fixed === side) {
      return { title: config.fixedLabel, hint: config.fixedHint, group: GROUP_IDS.BANK };
    }
    if (fixed) return { title: config.particularsLabel, hint: config.particularsHint, group: null };
    return side === SIDES.DEBIT
      ? { title: config.debitLabel || 'Debit', hint: config.debitHint, group: null }
      : { title: config.creditLabel || 'Credit', hint: config.creditHint, group: null };
  };

  return [SIDES.DEBIT, SIDES.CREDIT].map((side) => ({ side, ...named(side) }));
}

/**
 * May this ledger be PICKED on this side of this voucher type?
 *
 * TWO STRENGTHS OF RULE LIVE HERE, and the difference is the whole design.
 *
 *   HARD — `restrictToFlag` and `forbidFlag`. Facts about what the voucher type
 *   IS: a Contra that touches a supplier is not a Contra, a Journal that moves
 *   bank breaks the reconciliation. These are also refused by
 *   `validateVoucher`, because a voucher can arrive from a duplicate or an
 *   import without ever passing a picker, and they apply to both sides.
 *
 *   SOFT — `sideRules`. Facts about which side of THIS type an account
 *   normally belongs on: a Payment does not debit income, a Receipt does not
 *   credit an expense. These shape the picker and stop there. They are NOT
 *   validated, deliberately — an accountant with a case I have not thought of
 *   can still write it as a Journal, and a rule I am 95% sure of should cost
 *   somebody a detour, not a refusal.
 *
 * That is also why the caller must keep an already-chosen ledger visible even
 * when this returns false. Filtering a list is guidance; blanking a field
 * somebody already filled in, or silently dropping the ledger on a voucher
 * saved last year, is data loss.
 *
 * @param {Object|null} config  a VOUCHER_CONFIG entry
 * @param {Object}      ledger  an entry from `buildLedgerIndex`
 * @param {string}      [side]  SIDES.DEBIT | SIDES.CREDIT; omit for hard rules only
 */
export function ledgerAllowedOnSide(config, ledger, side = null) {
  if (!config || !ledger) return true;

  const { restrictToFlag, forbidFlag } = config;
  const has = (flag) => !!ledger.flags?.has?.(flag);
  if (restrictToFlag && !has(restrictToFlag)) return false;
  if (forbidFlag && has(forbidFlag)) return false;

  const rule = side ? config.sideRules?.[side] : null;
  if (!rule) return true;
  if (rule.forbidFlags?.some(has)) return false;
  if (rule.forbidNatures?.includes(ledger.nature)) return false;
  /* An allow-list, for a type that ever needs one. None does today. */
  if (rule.allowNatures && !rule.allowNatures.includes(ledger.nature)) return false;
  return true;
}

/* ------------------------------------------------------------------ *
 * Shape
 * ------------------------------------------------------------------ */

/**
 * A generous cap rather than a tight one.
 *
 * The specification asks for Tally-level freedom, and Tally does not stop you
 * at ten lines. This exists only so that a runaway loop cannot write a
 * document larger than Firestore's 1 MiB limit — at ~200 bytes a line, 200
 * lines is nowhere near it and is far past any hand-entered voucher.
 */
export const MAX_LINES = 200;

export function emptyLine(side = SIDES.DEBIT) {
  return {
    ledgerId: '',
    ledgerName: '',
    side,
    amount: 0,
    narration: '',
    /** Bill-wise references, for a party line. See BILL_REF_KINDS below. */
    billRefs: [],
    /** Cost centre / branch / project, when the business uses them. */
    costCentreId: null,
  };
}

/**
 * A blank voucher of a type.
 *
 * A DOUBLE voucher opens with two lines, because one line can never balance.
 * A SINGLE voucher opens with one particulars line and an empty fixed ledger,
 * because the second side is the type's own.
 */
export function emptyVoucher(type = VOUCHER_TYPES.JOURNAL, date = new Date()) {
  const config = voucherConfig(type);
  const isSingle = config.entryMode === ENTRY_MODES.SINGLE;

  return {
    type,
    date,
    number: '',
    reference: '',
    narration: '',
    /**
     * SINGLE only, and now only on vouchers SAVED BEFORE the editor showed
     * both sides as rows.
     *
     * It used to be the one cash/bank ledger the money moved through, held in
     * a field of its own while the other side lived in the grid. `expandVoucher`
     * still honours it — a stored voucher must keep posting what it posted —
     * but nothing sets it any more: the editor opens both sides as rows, which
     * is the only shape that can hold a payment made from two accounts.
     */
    fixedLedgerId: '',
    /**
     * A SINGLE voucher opens with a row on EACH side, not one.
     *
     * The missing second row was the fixed ledger's field. With both sides
     * entered as rows, opening with only the particulars row would leave the
     * "Paid from" card empty and the operator wondering whether it takes rows
     * at all.
     */
    lines: isSingle
      ? [
          emptyLine(config.fixedSide === SIDES.CREDIT ? SIDES.DEBIT : SIDES.CREDIT),
          emptyLine(config.fixedSide),
        ]
      : [emptyLine(SIDES.DEBIT), emptyLine(SIDES.CREDIT)],
    attachments: [],
  };
}

/* ------------------------------------------------------------------ *
 * Bill-wise references
 *
 * A payment of ₹50,000 against a supplier who has four bills open is not one
 * fact, it is four. Without the breakdown the party balance is right and the
 * ageing is wrong, which is the state most hand-kept books are in.
 * ------------------------------------------------------------------ */

export const BILL_REF_KINDS = {
  /** A brand-new bill — what a sales or purchase voucher creates. */
  NEW: 'new',
  /** Settling an existing bill, in whole or in part. */
  AGAINST: 'against',
  /** Money taken before any bill exists. */
  ADVANCE: 'advance',
  /** A balance carried forward with no bill detail behind it. */
  ON_ACCOUNT: 'on_account',
};

export const BILL_REF_KIND_LABEL = {
  [BILL_REF_KINDS.NEW]: 'New Reference',
  [BILL_REF_KINDS.AGAINST]: 'Against Reference',
  [BILL_REF_KINDS.ADVANCE]: 'Advance',
  [BILL_REF_KINDS.ON_ACCOUNT]: 'On Account',
};


/**
 * Whether a line's bill-wise breakdown adds up to the line.
 *
 * Returned rather than thrown, and NOT a blocking error: a part-referenced
 * payment is legal (₹30,000 against a bill, ₹20,000 on account) and the
 * remainder is treated as on-account. What is refused is a breakdown that
 * exceeds the line, because that allocates money that was not paid.
 */
export function billRefStatus(line) {
  const total = Math.abs(round(line?.amount || 0));
  const allocated = (line?.billRefs || []).reduce(
    (sum, ref) => sum + Math.abs(round(ref?.amount || 0)),
    0
  );
  return {
    total,
    allocated,
    unallocated: total - allocated,
    over: allocated > total,
    exact: allocated === total && total > 0,
  };
}

/* ------------------------------------------------------------------ *
 * Totals
 * ------------------------------------------------------------------ */

/** Whether a line has been filled in enough to post. */
export function lineIsComplete(line) {
  if (!line) return false;
  const amount = round(line.amount);
  return !!line.ledgerId && Number.isFinite(amount) && amount > 0;
}

/** Touched but not finished — worth complaining about rather than dropping. */
export function lineIsPartial(line) {
  if (!line || lineIsComplete(line)) return false;
  const amount = round(line.amount);
  return !!line.ledgerId || !!String(line.narration || '').trim() || amount > 0;
}

/**
 * Add up the two sides.
 *
 * `difference` is signed on purpose. "The credit side is ₹500 short" tells an
 * operator which box to change; "this does not balance" makes them read the
 * whole form again.
 */
export function voucherTotals(lines = []) {
  let debit = 0;
  let credit = 0;

  lines.forEach((line) => {
    const amount = round(line?.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    if (line.side === SIDES.CREDIT) credit += amount;
    else debit += amount;
  });

  return {
    debit,
    credit,
    difference: debit - credit,
    balanced: debit === credit && debit > 0,
  };
}

/**
 * Expand a SINGLE-entry voucher into its full set of lines.
 *
 * A payment of ₹10,000 to Electricity and ₹5,000 to Office Expense out of HDFC
 * is entered as two particulars lines and one "paid from" ledger. What is
 * POSTED is three lines: two debits and one credit of ₹15,000. Doing that
 * expansion HERE — once, in a pure function that both the screen's totals
 * panel and the repository call — is what stops the preview and the posting
 * disagreeing.
 */
export function expandVoucher(values = {}) {
  const config = voucherConfig(values.type);
  const lines = (values.lines || []).filter(lineIsComplete);

  if (config.entryMode !== ENTRY_MODES.SINGLE) return lines;
  if (!values.fixedLedgerId) return lines;

  /**
   * The balancing line is the DIFFERENCE, not the sum.
   *
   * Taking the sum would be right only if every particulars row were on the
   * same side, and forcing that would be exactly the restriction the
   * specification asks not to impose — a receipt that takes ₹50,000 from a
   * customer while writing off ₹500 of discount has rows on both sides and is
   * a perfectly ordinary entry. Using the difference means the grid stays free
   * and the fixed ledger takes whatever is left over.
   */
  const totals = voucherTotals(lines);
  const difference = totals.debit - totals.credit;
  if (difference === 0) return lines;

  const side = difference > 0 ? SIDES.CREDIT : SIDES.DEBIT;

  return [
    ...lines,
    {
      ...emptyLine(side),
      ledgerId: values.fixedLedgerId,
      side,
      amount: Math.abs(difference),
      narration: '',
      /* The contra-side line carries the bill references of NO line: they
         belong to the party rows, and duplicating them here would allocate
         every bill twice. */
      billRefs: [],
      isContraSide: true,
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Validation
 * ------------------------------------------------------------------ */

/**
 * Everything that must be true before a voucher is written.
 *
 * `context` carries what only the caller knows: the ledger index (so a
 * restriction can be checked), the locked periods and the financial year. All
 * three are optional — the pure rules still apply without them, and the screen
 * passes what it has.
 *
 * The messages are about what to DO. That is not a style preference: "Debits
 * are ₹500 more than credits" names the box to change, and "unbalanced entry"
 * makes somebody read the whole form again to find it.
 */
export function validateVoucher(values = {}, context = {}) {
  const { ledgerIndex = null, locks = [], allowFutureDates = false, role = '' } = context;
  const config = voucherConfig(values.type);
  const errors = {};

  /* --- date ------------------------------------------------------- */
  const date = toDate(values.date);
  if (!date) {
    errors.date = 'Give the voucher a date.';
  } else {
    if (!allowFutureDates && date.getTime() > endOfToday()) {
      errors.date = 'That date is in the future. Books are kept as things happen.';
    }
    /**
     * The lock check goes through `canPostOn` and NOT through `lockFor`.
     *
     * The difference is the override, and getting it wrong made the override
     * unreachable: the specification requires that an authorised user can
     * write into a locked period, the repository checked for that with
     * `canPostOn`, and this function had already refused the save before the
     * repository was reached. A rule enforced in two places has to be the same
     * rule in both.
     */
    const gate = canPostOn(date, { locks, role });
    if (!gate.ok) errors.date = gate.reason;
  }

  /* --- lines ------------------------------------------------------ */
  const raw = values.lines || [];
  const partial = raw.filter(lineIsPartial);
  const complete = raw.filter(lineIsComplete);

  if (partial.length > 0) {
    errors.lines =
      partial.length === 1
        ? 'One row is half filled in. Give it a ledger and an amount, or clear it.'
        : `${partial.length} rows are half filled in. Give each a ledger and an amount, or clear them.`;
  } else if (complete.length === 0) {
    errors.lines = 'Add at least one row.';
  } else if (raw.length > MAX_LINES) {
    errors.lines = `A single voucher cannot have more than ${MAX_LINES} rows.`;
  }

  /* --- the fixed side of a single-entry voucher --------------------
   *
   * "You have not said where the money came from or went to."
   *
   * TWO CONDITIONS, and the second one is newer than the first.
   *
   * Not balanced — a user who HAS written both sides by hand has said what
   * they meant, and asking them for a "paid from" account they do not need is
   * a box that cannot be filled in correctly.
   *
   * Nothing on the fixed side — this used to be implied by the first, back
   * when the fixed side could only ever be one ledger in one field. It is not
   * implied any more: the editor now enters that side as rows, so a payment
   * mid-typing (₹5,000 out, ₹3,000 of it accounted for) is unbalanced AND has
   * a paid-from row. Without this condition it would collect this error on top
   * of `errors.balance`, which already says the useful thing — how much is
   * missing — while this one would tell them to fill in a field that is not
   * on the screen. */
  if (config.entryMode === ENTRY_MODES.SINGLE && complete.length > 0 && !values.fixedLedgerId) {
    const onFixedSide = complete.some((line) => line.side === config.fixedSide);
    if (!onFixedSide && !voucherTotals(complete).balanced) {
      errors.fixedLedgerId = `Choose the account this was ${
        config.fixedSide === SIDES.CREDIT ? 'paid from' : 'received into'
      }.`;
    }
  }

  /* --- the one rule ----------------------------------------------- */
  const expanded = expandVoucher(values);
  const totals = voucherTotals(expanded);

  if (expanded.length > 0 && !totals.balanced) {
    if (totals.debit === 0 && totals.credit === 0) {
      errors.balance = 'Every row is zero. A voucher has to move something.';
    } else {
      const gap = Math.abs(totals.difference);
      const short = totals.difference > 0 ? 'credit' : 'debit';
      errors.balance = `Debit and Credit totals do not match. The ${short} side is short by ${formatMoney(
        gap
      )}.`;
    }
  }

  /* --- type restrictions ------------------------------------------
   *
   * THE THREE ACCOUNTING CONTROLS, ENFORCED HERE AND IN THE GRID.
   *
   * The grid filters what can be picked, which is where a user meets the rule;
   * this is where the rule actually holds. A voucher can reach this function
   * without ever passing through the picker — a duplicate of an older voucher,
   * an import, a ledger regrouped from Bank to Loans after the entry was
   * written — and a control that only exists in a dropdown is not a control.
   *
   *   restrictToFlag   only these ledgers may appear.    Contra: cash/bank only.
   *   forbidFlag       these ledgers may NOT appear.     Journal: no cash/bank.
   *   requiresCashSide at least one must be cash/bank.   Payment and Receipt.
   */
  if (config.restrictToFlag && ledgerIndex) {
    const offending = expanded.find((line) => {
      const ledger = ledgerIndex.get(line.ledgerId);
      return ledger && !ledger.flags?.has(config.restrictToFlag);
    });
    if (offending) {
      const ledger = ledgerIndex.get(offending.ledgerId);
      errors.restriction = `${ledger?.name || 'That ledger'} is not a cash or bank account. ${
        config.restrictionMessage || ''
      }`.trim();
    }
  }

  if (config.forbidFlag && ledgerIndex) {
    const offending = expanded.find((line) => {
      const ledger = ledgerIndex.get(line.ledgerId);
      return ledger && ledger.flags?.has(config.forbidFlag);
    });
    if (offending) {
      const ledger = ledgerIndex.get(offending.ledgerId);
      errors.restriction = `${ledger?.name || 'That ledger'} is a cash or bank account. ${
        config.forbidMessage || ''
      }`.trim();
    }
  }

  /* `!errors.fixedLedgerId` because the "choose the account this was paid
     from" message above is the more specific answer when the grid simply does
     not balance yet. This one is for the case that message never caught: rows
     that DO balance, between ledgers none of which is cash or bank. */
  if (config.requiresCashSide && ledgerIndex && expanded.length > 0 && !errors.fixedLedgerId) {
    const hasCash = expanded.some((line) =>
      ledgerIndex.get(line.ledgerId)?.flags?.has(GROUP_FLAGS.CASH)
    );
    if (!hasCash) {
      errors.fixedLedgerId =
        config.cashSideMessage || 'One side of this voucher must be a cash or bank account.';
    }
  }

  /* --- a ledger both debited and credited -------------------------- */
  const clash = duplicateLedger(expanded);
  if (clash) {
    errors.duplicate = `${clash} is both debited and credited in this voucher, which nets to nothing.`;
  }

  /* --- bill-wise over-allocation ----------------------------------- */
  const overAllocated = complete.find((line) => billRefStatus(line).over);
  if (overAllocated) {
    errors.billRefs =
      'One row allocates more against bills than the row itself is worth. Reduce the allocation.';
  }

  /* --- advisory, never blocking ------------------------------------ */
  if (!String(values.narration || '').trim()) {
    errors.narrationHint =
      'Say what this is for. An entry with no narration is unreadable a year later.';
  }
  /* `warnOnCash` used to live here as an advisory hint on the Journal. It is
     now `forbidFlag` above — refused rather than mentioned — so there is
     nothing left to warn about. The `cashHint` key is kept out of the errors
     object entirely; the editor renders it only when present. */

  /**
   * A LEDGER ENTRY IS NOT A STOCK MOVEMENT, and Stock-in-Hand is the one head
   * where a reader will assume it is.
   *
   * Debiting Stock-in-Hand by ₹50,000 changes what the Balance Sheet says the
   * shelf is worth and moves not one unit of anything. Nothing is wrong with
   * doing it — a year-end valuation adjustment is exactly that entry — but a
   * shopkeeper who posts it expecting the item list to follow has silently put
   * the two out of agreement, and the stock reports will not mention it.
   *
   * Advisory, like the cash hint: the entry is legitimate and refusing it would
   * send somebody to a spreadsheet. It only has to be SAID.
   */
  if (ledgerIndex) {
    const stockLine = expanded.find((line) => ledgerIndex.get(line.ledgerId)?.isStock);
    if (stockLine) {
      errors.stockHint =
        'This moves the Stock-in-Hand ledger, which changes what the Balance Sheet says your stock is worth. It does not move any item — record a stock adjustment for that.';
    }
  }

  return errors;
}

/** Errors that actually block a save. The `*Hint` keys deliberately do not. */
export function blockingErrors(errors = {}) {
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !key.endsWith('Hint')));
}


function duplicateLedger(lines = []) {
  const sides = new Map();
  for (const line of lines) {
    const seen = sides.get(line.ledgerId);
    if (seen && seen !== line.side) return line.ledgerName || 'That ledger';
    if (!seen) sides.set(line.ledgerId, line.side);
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Period locking
 *
 * A lock is a date range plus the role that may still write inside it. The
 * specification asks for specific dates, ranges, months and whole financial
 * years; all four are the same thing once expressed as a range, so there is one
 * shape rather than four.
 * ------------------------------------------------------------------ */

/** Find the lock covering a date, or null. */
export function lockFor(date, locks = []) {
  const time = toDate(date)?.getTime();
  if (time === undefined || time === null) return null;

  return (
    locks.find((lock) => {
      if (!lock || lock.isReleased) return false;
      const from = toDate(lock.from)?.getTime() ?? -Infinity;
      const to = toDate(lock.to)?.getTime() ?? Infinity;
      return time >= from && time <= to;
    }) || null
  );
}

export function lockMessage(lock) {
  if (!lock) return '';
  const reason = lock.reason ? ` (${lock.reason})` : '';
  return `That date falls in a locked period${reason}. An owner can reopen it in Settings › Accounting.`;
}

/**
 * Whether a role may write on a date.
 *
 * A lock is not absolute — the specification requires that authorised users can
 * reopen a period, and a lock nobody can override is a lock that gets worked
 * around by back-dating the next voucher instead.
 */
export function canPostOn(date, { locks = [], role = '', overrideRoles = ['owner'] } = {}) {
  const lock = lockFor(date, locks);
  if (!lock) return { ok: true, lock: null };
  if (overrideRoles.includes(role)) return { ok: true, lock, overridden: true };
  return { ok: false, lock, reason: lockMessage(lock) };
}

/** Whether a date sits inside a financial year that is still open. */
export function financialYearFor(date) {
  return financialYear(toDate(date) || new Date());
}

/* ------------------------------------------------------------------ *
 * Turning a voucher into entries
 *
 * This is the function the whole module exists for. Everything above shapes and
 * checks a voucher; this makes the rows that the ledgers, the trial balance and
 * every report are derived from.
 *
 * The entries are RESOLVED — the ledger's name and its group are copied onto
 * each row at the moment of posting. A statement read in three years must still
 * say "Bank of Baroda" after that ledger has been renamed, and a report that
 * had to join every row back to a master would be one read per row.
 * ------------------------------------------------------------------ */

/**
 * @param {Object} voucher   the voucher AS STORED (already expanded)
 * @param {Map} ledgerIndex  id -> indexed ledger (see lib/ledgers buildLedgerIndex)
 * @returns {Array} entries, one per line, in line order
 */
export function deriveEntries(voucher, ledgerIndex) {
  const lines = voucher.lines || [];

  return lines.map((line, index) => {
    const ledger = ledgerIndex?.get?.(line.ledgerId) || null;
    const signed = signedAmount(line);

    return {
      /** Where it came from, so a report row can open the original. */
      voucherId: voucher.id || null,
      voucherType: voucher.type,
      voucherNumber: voucher.number || '',
      lineIndex: index,

      ledgerId: line.ledgerId,
      ledgerName: line.ledgerName || ledger?.name || '',
      groupId: ledger?.groupId || null,
      nature: ledger?.nature || null,

      side: line.side === SIDES.CREDIT ? SIDES.CREDIT : SIDES.DEBIT,
      amount: Math.abs(round(line.amount)),
      /** Positive is a debit. The one convention, see lib/ledgers. */
      signedAmount: signed,

      date: voucher.date,
      financialYear: voucher.financialYear || financialYearFor(voucher.date).key,
      narration: line.narration || voucher.narration || '',
      reference: voucher.reference || '',

      /** A party line carries the counterparty, for the outstanding reports. */
      partyLedgerId: ledger?.isParty ? line.ledgerId : null,
      billRefs: line.billRefs || [],
      costCentreId: line.costCentreId || null,

      /** Set when the voucher is the shadow of a billing document. */
      sourceType: voucher.sourceType || null,
      sourceId: voucher.sourceId || null,

      isCancelled: !!voucher.isCancelled,
    };
  });
}

/**
 * The net movement each ledger gets from a set of entries.
 *
 * Returned as a Map so the repository can turn it straight into one
 * `increment()` per ledger, rather than one per line — a voucher that debits
 * the same expense twice must not write two increments to the same document in
 * one transaction, which Firestore refuses.
 */
export function movementsByLedger(entries = []) {
  const movements = new Map();
  entries.forEach((entry) => {
    const current = movements.get(entry.ledgerId) || { signed: 0, debit: 0, credit: 0 };
    current.signed += entry.signedAmount;
    if (entry.side === SIDES.CREDIT) current.credit += entry.amount;
    else current.debit += entry.amount;
    movements.set(entry.ledgerId, current);
  });
  return movements;
}

/* ------------------------------------------------------------------ *
 * Describing a voucher
 * ------------------------------------------------------------------ */

/**
 * A one-line summary for a register row.
 *
 * Named ledgers rather than amounts, because a list is scanned for "which one
 * was the depreciation" and never for "which one was ₹4,000".
 */
export function describeVoucher(voucher) {
  const lines = voucher?.lines || [];
  const debits = lines.filter((l) => l.side !== SIDES.CREDIT).map((l) => l.ledgerName).filter(Boolean);
  const credits = lines.filter((l) => l.side === SIDES.CREDIT).map((l) => l.ledgerName).filter(Boolean);
  if (!debits.length && !credits.length) return '';
  return `${debits.join(', ') || '—'} → ${credits.join(', ') || '—'}`;
}


/* ------------------------------------------------------------------ *
 * Small shared helpers
 * ------------------------------------------------------------------ */

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function endOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
}
